import { type PropsWithChildren, useCallback, useEffect, useState } from 'react'

import {
  type FormikChildListenerProviderContextType,
  Provider,
  useOptionalFormikChildListenerContext,
} from './formikChildListenerContext'
import { useOptionalShowChangesButtonContext } from './showChangesButtonContext'

type Props = PropsWithChildren

/**
 * Context Provider for managing mounted formik fields in a forms.
 */
const FormikChildListenerProvider = ({ children }: Props) => {
  const [mountedFieldNames, setmountedFieldNames] = useState<string[]>([])
  const { onFormikChildChange } = useOptionalFormikChildListenerContext()
  const { shouldBeVisible, onChangeVisibility } =
    useOptionalShowChangesButtonContext()

  useEffect(() => {
    if (!shouldBeVisible && mountedFieldNames.length > 0) {
      onChangeVisibility?.(true)
    }
  }, [onChangeVisibility, shouldBeVisible, mountedFieldNames.length])

  const handleFormikKeyArray: FormikChildListenerProviderContextType['onFormikChildChange'] =
    useCallback(
      ({ name, action }) => {
        if (action === 'add') {
          setmountedFieldNames((currentValues) =>
            Array.from(new Set([...currentValues, name])),
          )
        } else if (action === 'remove') {
          setmountedFieldNames((currentValues) =>
            Array.from(
              new Set([...currentValues.filter((key) => key !== name)]),
            ),
          )
        }
        onFormikChildChange?.({ name, action })
      },
      [onFormikChildChange],
    )

  return (
    <Provider
      value={{
        mountedFieldNames,
        onFormikChildChange: handleFormikKeyArray,
      }}
    >
      {children}
    </Provider>
  )
}

export { FormikChildListenerProvider }
